import React, { useState } from 'react';
import { Form, Row, Col, Alert, Collapse } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { updatePartSettings } from '../store/appSlice';
import { PartSettings } from '../types';
import { YOUTUBE_RATES, snapToYouTubeRate } from '../services/youtubeEngine';

/** Returns the unique BPMs a YouTube track will actually play at across all snapped rates. */
function getAvailableYouTubeBpms(trackBpm: number): number[] {
  return YOUTUBE_RATES.map((r) => Math.round(trackBpm * r));
}

const getDefaultSettings = (): PartSettings => ({
  autoSpeedUp: false,
  startBpm: 60,
  targetBpm: 120,
  increaseBpm: 5,
  pitchShift: 0,
  duration: 120, // 2 minutes default
  fixedBpm: undefined,
});

interface BackingTrackSettingsProps {
  isYouTubeTrack?: boolean;
  trackBpm?: number;
}

const BackingTrackSettings: React.FC<BackingTrackSettingsProps> = ({ isYouTubeTrack = false, trackBpm }) => {
  const dispatch = useAppDispatch();
  const { selectedTrackId, selectedPartIndex, partSettings } = useAppSelector((state) => state.app.backingTrack);
  const [open, setOpen] = useState(false);

  // Get current settings for selected track and part
  
  const currentSettings: PartSettings = 
    selectedTrackId && selectedPartIndex !== null && partSettings[selectedTrackId]?.[selectedPartIndex]
      ? partSettings[selectedTrackId][selectedPartIndex]
      : getDefaultSettings();

  const canEdit = selectedTrackId && selectedPartIndex !== null;

  const updateSettings = (updates: Partial<PartSettings>) => {
    if (!canEdit) return;
    dispatch(updatePartSettings({
      trackId: selectedTrackId!,
      partIndex: selectedPartIndex!,
      settings: updates,
    }));
  };

  const handleTempoModeChange = (mode: 'fixed' | 'auto') => {
    updateSettings({ autoSpeedUp: mode === 'auto' });
  };

  const snapBpmToYouTube = (bpm: number): number => {
    if (!isYouTubeTrack || !trackBpm) return bpm;
    return Math.round(trackBpm * snapToYouTubeRate(bpm / trackBpm));
  };

  const handleStartBpmBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = parseInt(e.target.value, 10);
    if (Number.isNaN(raw)) return;
    const snapped = snapBpmToYouTube(raw);
    if (snapped !== currentSettings.startBpm) {
      updateSettings({ startBpm: snapped });
    }
  };

  const handleTargetBpmBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = parseInt(e.target.value, 10);
    if (Number.isNaN(raw)) return;
    const snapped = snapBpmToYouTube(raw);
    if (snapped !== currentSettings.targetBpm) {
      updateSettings({ targetBpm: snapped });
    }
  };

  const handleStartBpmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSettings({ startBpm: parseInt(e.target.value, 10) });
  };

  const handleTargetBpmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSettings({ targetBpm: parseInt(e.target.value, 10) });
  };

  const handleIncreaseBpmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSettings({ increaseBpm: parseInt(e.target.value, 10) });
  };

  const handlePitchShiftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSettings({ pitchShift: parseInt(e.target.value, 10) });
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSettings({ duration: parseInt(e.target.value, 10) });
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="backing-track-settings">
      <button
        type="button"
        className="btn btn-link p-0 text-decoration-none d-flex align-items-center justify-content-between w-100 mb-3"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="backing-track-settings-collapse"
        style={{ color: 'inherit' }}
      >
        <h5 className="mb-0">Settings</h5>
        <span aria-hidden="true" style={{ transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>
          ▶
        </span>
      </button>

      <Collapse in={open}>
        <div id="backing-track-settings-collapse">
          {!canEdit && (
            <p className="text-muted small">Select a track and part to configure settings</p>
          )}
      
      <Form.Group className="mb-3">
        <Form.Check
          inline
          type="radio"
          id="tempo-mode-fixed"
          name="tempoMode"
          label="Fixed BPM"
          checked={!currentSettings.autoSpeedUp}
          onChange={() => handleTempoModeChange('fixed')}
          disabled={!canEdit}
        />
        <Form.Check
          inline
          type="radio"
          id="tempo-mode-auto"
          name="tempoMode"
          label="Auto Speed Up"
          checked={currentSettings.autoSpeedUp}
          onChange={() => handleTempoModeChange('auto')}
          disabled={!canEdit}
        />
      </Form.Group>

      {currentSettings.autoSpeedUp && (
        <>
          <Form.Group className="mb-3">
            <Form.Text className="text-muted">
              Gradually increases tempo after each loop until target BPM is reached.
            </Form.Text>
          </Form.Group>
        
        <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Start BPM</Form.Label>
                <Form.Control
                  type="number"
                  value={currentSettings.startBpm}
                  onChange={handleStartBpmChange}
                  onBlur={handleStartBpmBlur}
                  min={20}
                  max={300}
                  disabled={!canEdit}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Target BPM</Form.Label>
                <Form.Control
                  type="number"
                  value={currentSettings.targetBpm}
                  onChange={handleTargetBpmChange}
                  onBlur={handleTargetBpmBlur}
                  min={20}
                  max={300}
                  disabled={!canEdit}
                />
              </Form.Group>
            </Col>
          </Row>

          {isYouTubeTrack && trackBpm && (() => {
            const allBpms = getAvailableYouTubeBpms(trackBpm);
            return (
              <div className="mb-3 p-2 border rounded bg-light">
                <p className="text-muted small mb-0">
                  <strong>YouTube available BPMs for this track:</strong>{' '}
                  {allBpms.join(', ')} BPM
                  <p className="text-info">
                    For more accurate speeds, add a local MP3 file
                  </p>
                </p>
              </div>
            );
          })()}

          <Form.Group className="mb-3">
            <Form.Label>Increase by BPM</Form.Label>
            <Form.Control
              type="number"
              value={currentSettings.increaseBpm}
              onChange={handleIncreaseBpmChange}
              min={1}
              max={50}
              disabled={!canEdit}
            />
          </Form.Group>
        </>
      )}

      <Form.Group className="mb-3">
        <Form.Label>Practice Duration: {formatDuration(currentSettings.duration)}</Form.Label>
        <Form.Range
          value={currentSettings.duration}
          onChange={handleDurationChange}
          min={30}
          max={1800}
          step={30}
          disabled={!canEdit}
        />
        <Form.Text className="text-muted">
          Set the practice duration for this part (30 seconds to 30 minutes)
        </Form.Text>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label> Pitch Shift (semitones): {currentSettings.pitchShift > 0 ? '+' : ''}{currentSettings.pitchShift}</Form.Label>
        <Form.Range
          value={currentSettings.pitchShift}
          onChange={handlePitchShiftChange}
          min={-12}
          max={12}
          step={1}
          disabled={!canEdit || isYouTubeTrack}
        />
        <Form.Text className={isYouTubeTrack ? 'text-warning' : 'text-muted'}>
          <p>Adjust pitch without changing tempo (useful for songs tuned differently). {' '}</p>
          {isYouTubeTrack && (
            <Alert variant="warning" className="py-2 small mb-3">
              Pitch shift is not supported for YouTube playback. Add a local MP3 in the "tracks" section to enable this feature.
            </Alert>
            )}
        </Form.Text>
      </Form.Group>
        </div>
      </Collapse>
    </div>
  );
};

export default BackingTrackSettings;
